import { useState, useEffect } from 'react';
import {
  Box, Typography, Button, Grid, TextField, FormControlLabel, Switch,
  CircularProgress, Alert, IconButton, MenuItem, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, Chip, Divider,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import api from '../../config/api';
import { useTranslation } from 'react-i18next';
import { formatDate } from '../formatDate';
import useStore from '../../config/store';
import { InfoTip } from '../LabelWithInfo';
import ErpAmountField from '../ErpAmountField';
import { parseAmount } from '../parseAmount';
import { SERVICE_MODES, normalizeServiceType, toServiceTypeValue } from '../serviceModes';
import { amountToInput, isEmptyAmount, isInvalidAmount } from '../quotations/quotationUtils';

// Provisiones calculadas por la cotización (fase 4b del cotizador OUT/EST,
// 2026-10-13). 'default' = según la modalidad de la cotización (aviso previo
// e indemnización: OUT sí, EST no; reemplazo de vacaciones: no)
const FORMULA_PROVISIONS = ['notice', 'severance', 'vacationReplacement'];
const FORMULA_MODES = ['default', 'include', 'exclude'];

const emptyForm = {
  margins: [],
  marginMinimum: '',
  operationalExpensesEnabled: false,
  operationalExpensesAmount: '',
  adminExpenseEnabled: false,
  adminExpenseAmount: '',
  assistanceCostEnabled: false,
  assistanceCostAmount: '',
  formulaProvisions: { notice: 'default', severance: 'default', vacationReplacement: 'default' },
  fixedProvisions: [],
  paymentTermDays: '',
  paymentDay: '',
  paymentNotes: '',
  usesPurchaseOrder: false,
};

const toForm = (agreement) => ({
  // Tipo de servicio: select (2026-10-13). Un texto antiguo reconocible pasa
  // al código; si no, queda tal cual como opción "texto anterior".
  margins: (agreement.margins || []).map((m) => ({
    serviceType: toServiceTypeValue(m.serviceType),
    feeType: m.feeType || 'percentage',
    value: amountToInput(m.value),
  })),
  marginMinimum: amountToInput(agreement.marginMinimum),
  operationalExpensesEnabled: Boolean(agreement.operationalExpenses?.enabled),
  operationalExpensesAmount: amountToInput(agreement.operationalExpenses?.amount),
  adminExpenseEnabled: Boolean(agreement.adminExpense?.enabled),
  adminExpenseAmount: amountToInput(agreement.adminExpense?.amount),
  assistanceCostEnabled: Boolean(agreement.assistanceCost?.enabled),
  assistanceCostAmount: amountToInput(agreement.assistanceCost?.amount),
  formulaProvisions: Object.fromEntries(FORMULA_PROVISIONS.map((key) => {
    const mode = agreement.formulaProvisions?.[key];
    return [key, FORMULA_MODES.includes(mode) ? mode : 'default'];
  })),
  fixedProvisions: (agreement.fixedProvisions || []).map((p) => ({
    name: p.name || '',
    amount: amountToInput(p.amount),
    prorated: p.prorated === true,
  })),
  paymentTermDays: agreement.paymentCondition?.termDays ?? '',
  paymentDay: agreement.paymentCondition?.paymentDay ?? '',
  paymentNotes: agreement.paymentCondition?.notes ?? '',
  usesPurchaseOrder: Boolean(agreement.usesPurchaseOrder),
});

// Días (plazo, día de pago) siguen siendo enteros simples
const toNumberOrNull = (value) =>
  value === '' || value === null || value === undefined ? null : Number(value);
// Montos y margen: formato chileno ("600.000" = 600000, "10,5" = 10.5)
const toAmountOrNull = (value) => parseAmount(value);

const toDateLabel = (value) => formatDate(value); // DD/MM/AAAA (CLAUDE.md → Fechas)

const costCenterLabel = (c) => (c.code ? `${c.code} — ${c.name}` : c.name);

/**
 * Acuerdo comercial del cliente (PLAN_ERP.md §4.3). Guardar una nueva versión
 * inactiva la anterior (historial); las cotizaciones futuras copian snapshot,
 * así que editar aquí nunca muta cotizaciones existentes.
 *
 * Fase 4 del cotizador OUT/EST (2026-10-13): un acuerdo para todo el cliente
 * y, opcionalmente, uno por centro de costo (reemplaza al del cliente en las
 * cotizaciones de ese centro de costo); uno vigente por alcance. Se agregan
 * margen mínimo, gastos operacionales y provisiones (calculadas: incluir /
 * excluir / según la modalidad; y de monto fijo, íntegras o proporcionales).
 */
const ErpAgreementTab = ({ clientId }) => {
  const { t } = useTranslation();
  const { organization, authToken, showAlert } = useStore.getState();

  const [agreements, setAgreements] = useState([]);
  const [costCenters, setCostCenters] = useState([]);
  // Alcance que se ve y edita: '' = todo el cliente; si no, uid del centro de costo
  const [scope, setScope] = useState('');
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const authHeaders = { headers: { Authorization: `Bearer ${authToken}` } };

  const load = async () => {
    try {
      setLoading(true);
      setError(null);

      const [agreementsRes, costCentersRes] = await Promise.allSettled([
        api.get(`/erp/clients/${clientId}/agreements`, { params: { organizationId: organization }, ...authHeaders }),
        api.get(`/erp/clients/${clientId}/cost-centers`, { params: { organizationId: organization }, ...authHeaders }),
      ]);

      if (agreementsRes.status === 'fulfilled' && agreementsRes.value.data.response?.status_response === 'OK') {
        setAgreements(agreementsRes.value.data.response.data || []);
      } else {
        if (agreementsRes.status === 'rejected') console.error(agreementsRes.reason);
        setError(t('erp.agreement.loadError'));
      }
      // Sin centros de costo se sigue pudiendo editar el acuerdo de todo el cliente
      if (costCentersRes.status === 'fulfilled') {
        setCostCenters(costCentersRes.value.data.response?.data || []);
      } else {
        console.error(costCentersRes.reason);
        setCostCenters([]);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (organization && clientId) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organization, clientId]);

  const activeFor = (scopeId) =>
    agreements.find((a) => a.status === 'active' && (a.costCenterId || '') === scopeId) || null;
  const active = activeFor(scope);
  const clientAgreement = activeFor('');

  // Al cambiar de alcance (o recargar): el acuerdo vigente de ese alcance. Un
  // centro de costo sin acuerdo propio parte de los valores del de todo el cliente.
  useEffect(() => {
    if (active) setForm(toForm(active));
    else if (scope && clientAgreement) setForm(toForm(clientAgreement));
    else setForm(emptyForm);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope, agreements]);

  // Centros de costo activos + los que tengan acuerdo vigente (aunque hoy no estén activos)
  const scopeOptions = costCenters.filter((c) => c.status === 'active' || activeFor(c.uid));
  agreements
    .filter((a) => a.status === 'active' && a.costCenterId && !scopeOptions.some((c) => c.uid === a.costCenterId))
    .forEach((a) => scopeOptions.push({ uid: a.costCenterId, code: '', name: a.costCenterName || a.costCenterId }));

  const setField = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const setMargin = (index, field, value) =>
    setForm((prev) => ({
      ...prev,
      margins: prev.margins.map((m, i) => (i === index ? { ...m, [field]: value } : m)),
    }));

  // Nueva fila: la primera modalidad que todavía no tiene margen
  const addMargin = () =>
    setForm((prev) => {
      const used = prev.margins.map((m) => m.serviceType);
      const nextMode = SERVICE_MODES.find((mode) => !used.includes(mode)) || '';
      return {
        ...prev,
        margins: [...prev.margins, { serviceType: nextMode, feeType: 'percentage', value: '' }],
      };
    });

  const serviceTypeLabel = (value) => {
    const code = normalizeServiceType(value);
    return code ? t(`erp.quotations.serviceMode.${code}`) : value || '—';
  };

  const removeMargin = (index) =>
    setForm((prev) => ({ ...prev, margins: prev.margins.filter((_, i) => i !== index) }));

  const setFormula = (key, mode) =>
    setForm((prev) => ({ ...prev, formulaProvisions: { ...prev.formulaProvisions, [key]: mode } }));

  const setFixed = (index, field, value) =>
    setForm((prev) => ({
      ...prev,
      fixedProvisions: prev.fixedProvisions.map((p, i) => (i === index ? { ...p, [field]: value } : p)),
    }));

  const addFixed = () =>
    setForm((prev) => ({
      ...prev,
      fixedProvisions: [...prev.fixedProvisions, { name: '', amount: '', prorated: false }],
    }));

  const removeFixed = (index) =>
    setForm((prev) => ({ ...prev, fixedProvisions: prev.fixedProvisions.filter((_, i) => i !== index) }));

  const toggleAmount = (enabled, amount) => ({
    enabled,
    amount: enabled ? toAmountOrNull(amount) : null,
  });

  const buildPayload = () => ({
    organizationId: organization,
    // Una fila sin valor no se guarda (la fila nueva ya trae modalidad elegida)
    margins: form.margins
      .filter((m) => m.value !== '' && m.value !== null && m.value !== undefined)
      .map((m) => ({
        serviceType: m.serviceType.trim(),
        feeType: m.feeType,
        value: toAmountOrNull(m.value),
      })),
    // Vacío = el margen mínimo de Configuración → Parámetros
    marginMinimum: toAmountOrNull(form.marginMinimum),
    operationalExpenses: toggleAmount(form.operationalExpensesEnabled, form.operationalExpensesAmount),
    adminExpense: toggleAmount(form.adminExpenseEnabled, form.adminExpenseAmount),
    assistanceCost: toggleAmount(form.assistanceCostEnabled, form.assistanceCostAmount),
    formulaProvisions: form.formulaProvisions,
    // Una fila sin nombre ni monto no se guarda
    fixedProvisions: form.fixedProvisions
      .filter((p) => p.name.trim() || p.amount !== '')
      .map((p) => ({ name: p.name.trim(), amount: toAmountOrNull(p.amount), prorated: p.prorated === true })),
    paymentCondition: {
      termDays: toNumberOrNull(form.paymentTermDays),
      paymentDay: toNumberOrNull(form.paymentDay),
      notes: form.paymentNotes || null,
    },
    usesPurchaseOrder: form.usesPurchaseOrder,
    currency: 'CLP',
  });

  // Guardar: si hay acuerdo vigente en el alcance se edita en su lugar;
  // "nueva versión" crea otro y el vigente de ese alcance pasa a historial
  // (lo resuelve el backend). Un acuerdo nuevo nace con el alcance elegido.
  const handleSave = async (asNewVersion = false) => {
    try {
      setSaving(true);

      const payload = buildPayload();

      const response = active && !asNewVersion
        ? await api.put(`/erp/agreements/${active.uid}`, payload, authHeaders)
        : await api.post(`/erp/clients/${clientId}/agreements`, { ...payload, costCenterId: scope || null }, authHeaders);

      if (response.data.response?.status_response === 'OK') {
        showAlert(t('erp.agreement.saveSuccess'), 'success');
        load();
      } else {
        showAlert(t('erp.agreement.saveError'), 'error');
      }
    } catch (err) {
      console.error(err);
      showAlert(t('erp.agreement.saveError'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const switchLabel = (labelKey, tooltipKey) => (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
      {t(labelKey)}
      <InfoTip title={t(tooltipKey)} />
    </Box>
  );

  // Montos que no se entienden como número ("12%", "5 mil"): en rojo y no se guarda
  const amountError = (raw) => (isInvalidAmount(raw)
    ? { error: true, helperText: t('erp.quotations.conditions.invalidAmount') }
    : {});
  const hasInvalidAmounts = [
    ...form.margins.map((m) => m.value),
    form.marginMinimum,
    form.operationalExpensesEnabled ? form.operationalExpensesAmount : '',
    form.adminExpenseEnabled ? form.adminExpenseAmount : '',
    form.assistanceCostEnabled ? form.assistanceCostAmount : '',
    ...form.fixedProvisions.map((p) => p.amount),
  ].some(isInvalidAmount);
  // Monto que falta: interruptor encendido o provisión fija con nombre y sin
  // monto (quedaría en $0 sin avisar). En rojo y no se guarda (2026-10-14)
  const missingError = { error: true, helperText: t('erp.quotations.conditions.missingAmount') };
  const toggleAmountError = (raw) => (isEmptyAmount(raw) ? missingError : amountError(raw));
  const fixedMissing = (p) => Boolean(String(p.name || '').trim()) && isEmptyAmount(p.amount);
  const hasMissingAmounts = [
    form.operationalExpensesEnabled && isEmptyAmount(form.operationalExpensesAmount),
    form.adminExpenseEnabled && isEmptyAmount(form.adminExpenseAmount),
    form.assistanceCostEnabled && isEmptyAmount(form.assistanceCostAmount),
    ...form.fixedProvisions.map(fixedMissing),
  ].some(Boolean);

  const scopeName = (agreement) => (agreement.costCenterId
    ? agreement.costCenterName || agreement.costCenterId
    : t('erp.agreement.scopeClient'));

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (error) return <Alert severity="error">{error}</Alert>;

  return (
    <Box>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6}>
          {/* "Todo el cliente" es el valor vacío: sin displayEmpty el select no
              lo muestra y la etiqueta tapa el campo (2026-10-14) */}
          <TextField
            select
            fullWidth
            size="small"
            label={t('erp.agreement.scope')}
            value={scope}
            onChange={(e) => setScope(e.target.value)}
            helperText={t('erp.tooltips.agreement.scope')}
            SelectProps={{ displayEmpty: true }}
            InputLabelProps={{ shrink: true }}
          >
            <MenuItem value="">
              {t('erp.agreement.scopeClient')}
              {clientAgreement ? '' : ` (${t('erp.agreement.scopeNoAgreement')})`}
            </MenuItem>
            {scopeOptions.map((c) => (
              <MenuItem key={c.uid} value={c.uid}>
                {costCenterLabel(c)}
                {activeFor(c.uid) ? ` · ${t('erp.agreement.scopeOwn')}` : ''}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
      </Grid>

      {!active && !scope && <Alert severity="info" sx={{ mb: 2 }}>{t('erp.agreement.none')}</Alert>}
      {!active && scope && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {t(clientAgreement ? 'erp.agreement.scopeInherits' : 'erp.agreement.scopeNone')}
        </Alert>
      )}

      <Typography variant="subtitle1" fontWeight={700}>
        {t('erp.agreement.margins.title')}
        <InfoTip title={t('erp.tooltips.agreement.margins')} />
      </Typography>
      <Divider sx={{ mt: 0.5, mb: 2 }} />

      {form.margins.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {t('erp.agreement.margins.empty')}
        </Typography>
      )}

      {form.margins.map((margin, index) => (
        <Grid container spacing={2} key={index} sx={{ mb: 1 }} alignItems="flex-start">
          <Grid item xs={12} sm={4}>
            <TextField
              select
              fullWidth
              size="small"
              label={t('erp.agreement.margins.serviceType')}
              value={margin.serviceType}
              onChange={(e) => setMargin(index, 'serviceType', e.target.value)}
              helperText={index === 0 ? t('erp.tooltips.agreement.serviceType') : ''}
            >
              {margin.serviceType && !SERVICE_MODES.includes(margin.serviceType) && (
                <MenuItem value={margin.serviceType} sx={{ color: '#E65100' }}>
                  {t('erp.agreement.margins.legacy', { value: margin.serviceType })}
                </MenuItem>
              )}
              {SERVICE_MODES.map((mode) => (
                <MenuItem
                  key={mode}
                  value={mode}
                  disabled={form.margins.some((m, i) => i !== index && m.serviceType === mode)}
                >
                  {t(`erp.quotations.serviceMode.${mode}`)}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              select
              fullWidth
              size="small"
              label={t('erp.agreement.margins.feeType')}
              value={margin.feeType}
              onChange={(e) => setMargin(index, 'feeType', e.target.value)}
            >
              <MenuItem value="percentage">{t('erp.agreement.margins.percentage')}</MenuItem>
              <MenuItem value="fixed">{t('erp.agreement.margins.fixed')}</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={10} sm={4}>
            <ErpAmountField
              label={t('erp.agreement.margins.value')}
              value={margin.value}
              {...amountError(margin.value)}
              onChange={(e) => setMargin(index, 'value', e.target.value)}
            />
          </Grid>
          <Grid item xs={2} sm={1}>
            <IconButton size="small" onClick={() => removeMargin(index)}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Grid>
        </Grid>
      ))}

      <Button size="small" startIcon={<AddIcon />} onClick={addMargin} sx={{ textTransform: 'none', mb: 2 }}>
        {t('erp.agreement.margins.add')}
      </Button>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={4}>
          <ErpAmountField
            label={t('erp.agreement.marginMinimum')}
            helperText={t('erp.agreement.marginMinimumHint')}
            {...amountError(form.marginMinimum)}
            value={form.marginMinimum}
            onChange={(e) => setField('marginMinimum', e.target.value)}
          />
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={12} sm={4}>
          <FormControlLabel
            control={(
              <Switch
                checked={form.operationalExpensesEnabled}
                onChange={(e) => setField('operationalExpensesEnabled', e.target.checked)}
              />
            )}
            label={switchLabel('erp.agreement.operationalExpenses', 'erp.tooltips.agreement.operationalExpenses')}
          />
          {form.operationalExpensesEnabled && (
            <ErpAmountField
              label={t('erp.agreement.operationalExpensesAmount')}
              {...toggleAmountError(form.operationalExpensesAmount)}
              value={form.operationalExpensesAmount}
              onChange={(e) => setField('operationalExpensesAmount', e.target.value)}
            />
          )}
        </Grid>
        <Grid item xs={12} sm={4}>
          <FormControlLabel
            control={(
              <Switch
                checked={form.adminExpenseEnabled}
                onChange={(e) => setField('adminExpenseEnabled', e.target.checked)}
              />
            )}
            label={switchLabel('erp.agreement.adminExpense', 'erp.tooltips.agreement.adminExpense')}
          />
          {form.adminExpenseEnabled && (
            <ErpAmountField
              label={t('erp.agreement.adminExpenseAmount')}
              {...toggleAmountError(form.adminExpenseAmount)}
              value={form.adminExpenseAmount}
              onChange={(e) => setField('adminExpenseAmount', e.target.value)}
            />
          )}
        </Grid>
        <Grid item xs={12} sm={4}>
          <FormControlLabel
            control={(
              <Switch
                checked={form.assistanceCostEnabled}
                onChange={(e) => setField('assistanceCostEnabled', e.target.checked)}
              />
            )}
            label={switchLabel('erp.agreement.assistanceCost', 'erp.tooltips.agreement.assistanceCost')}
          />
          {form.assistanceCostEnabled && (
            <ErpAmountField
              label={t('erp.agreement.assistanceCostAmount')}
              {...toggleAmountError(form.assistanceCostAmount)}
              value={form.assistanceCostAmount}
              onChange={(e) => setField('assistanceCostAmount', e.target.value)}
            />
          )}
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            size="small"
            type="number"
            label={t('erp.agreement.paymentTermDays')}
            helperText={t('erp.tooltips.agreement.paymentTermDays')}
            value={form.paymentTermDays}
            onChange={(e) => setField('paymentTermDays', e.target.value)}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            size="small"
            type="number"
            label={t('erp.agreement.paymentDay')}
            helperText={t('erp.tooltips.agreement.paymentDay')}
            value={form.paymentDay}
            onChange={(e) => setField('paymentDay', e.target.value)}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <FormControlLabel
            control={(
              <Switch
                checked={form.usesPurchaseOrder}
                onChange={(e) => setField('usesPurchaseOrder', e.target.checked)}
              />
            )}
            label={switchLabel('erp.agreement.usesPurchaseOrder', 'erp.tooltips.agreement.usesPurchaseOrder')}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            size="small"
            multiline
            minRows={2}
            label={t('erp.agreement.paymentNotes')}
            value={form.paymentNotes}
            onChange={(e) => setField('paymentNotes', e.target.value)}
          />
        </Grid>
      </Grid>

      <Typography variant="subtitle1" fontWeight={700} sx={{ mt: 3 }}>
        {t('erp.agreement.provisions.title')}
        <InfoTip title={t('erp.tooltips.agreement.formulaProvisions')} />
      </Typography>
      <Divider sx={{ mt: 0.5, mb: 2 }} />

      <Grid container spacing={2} sx={{ mb: 2 }}>
        {FORMULA_PROVISIONS.map((key) => (
          <Grid item xs={12} sm={4} key={key}>
            <TextField
              select
              fullWidth
              size="small"
              label={t(`erp.agreement.provisions.${key}`)}
              helperText={t(`erp.agreement.provisions.formula.${key}`)}
              value={form.formulaProvisions[key]}
              onChange={(e) => setFormula(key, e.target.value)}
            >
              <MenuItem value="default">
                {t(key === 'vacationReplacement' ? 'erp.agreement.provisions.mode.defaultNo' : 'erp.agreement.provisions.mode.default')}
              </MenuItem>
              <MenuItem value="include">{t('erp.agreement.provisions.mode.include')}</MenuItem>
              <MenuItem value="exclude">{t('erp.agreement.provisions.mode.exclude')}</MenuItem>
            </TextField>
          </Grid>
        ))}
      </Grid>

      <Typography variant="body2" fontWeight={700} sx={{ mb: 1 }}>
        {t('erp.agreement.provisions.fixedTitle')}
        <InfoTip title={t('erp.tooltips.agreement.fixedProvisions')} />
      </Typography>
      {form.fixedProvisions.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {t('erp.agreement.provisions.fixedEmpty')}
        </Typography>
      )}
      {form.fixedProvisions.map((provision, index) => (
        <Grid container spacing={2} key={index} sx={{ mb: 1 }} alignItems="center">
          <Grid item xs={12} sm={5}>
            <TextField
              fullWidth
              size="small"
              label={t('erp.agreement.provisions.name')}
              value={provision.name}
              onChange={(e) => setFixed(index, 'name', e.target.value)}
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <ErpAmountField
              label={t('erp.agreement.provisions.amount')}
              {...(fixedMissing(provision) ? missingError : amountError(provision.amount))}
              value={provision.amount}
              onChange={(e) => setFixed(index, 'amount', e.target.value)}
            />
          </Grid>
          <Grid item xs={5} sm={3}>
            <FormControlLabel
              control={(
                <Switch
                  size="small"
                  checked={provision.prorated}
                  onChange={(e) => setFixed(index, 'prorated', e.target.checked)}
                />
              )}
              label={<Typography variant="body2">{t('erp.agreement.provisions.prorated')}</Typography>}
            />
          </Grid>
          <Grid item xs={1}>
            <IconButton size="small" aria-label={t('erp.agreement.provisions.remove')} onClick={() => removeFixed(index)}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Grid>
        </Grid>
      ))}
      <Button size="small" startIcon={<AddIcon />} onClick={addFixed} sx={{ textTransform: 'none' }}>
        {t('erp.agreement.provisions.add')}
      </Button>

      <Alert severity="info" sx={{ mt: 2 }}>{t('erp.agreement.newVersionInfo')}</Alert>
      {hasInvalidAmounts && (
        <Alert severity="error" sx={{ mt: 2 }}>{t('erp.agreement.invalidAmounts')}</Alert>
      )}
      {hasMissingAmounts && (
        <Alert severity="error" sx={{ mt: 2 }}>{t('erp.agreement.missingAmounts')}</Alert>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
        {active && (
          <Button
            variant="outlined"
            disabled={saving || hasInvalidAmounts || hasMissingAmounts}
            onClick={() => handleSave(true)}
            sx={{ textTransform: 'none' }}
          >
            {t('erp.agreement.newVersion')}
          </Button>
        )}
        <Button
          variant="contained"
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
          disabled={saving || hasInvalidAmounts || hasMissingAmounts}
          onClick={() => handleSave(false)}
          sx={{ backgroundColor: '#6A5AF9', '&:hover': { backgroundColor: '#5B4DE8' }, textTransform: 'none' }}
        >
          {active
            ? t('erp.common.save')
            : t(scope ? 'erp.agreement.createForCostCenter' : 'erp.agreement.create')}
        </Button>
      </Box>

      {agreements.length > 1 && (
        <Box sx={{ mt: 4 }}>
          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
            {t('erp.agreement.historyTitle')}
          </Typography>
          <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #E5E7EB' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('erp.common.status')}</TableCell>
                  <TableCell>{t('erp.agreement.historyScope')}</TableCell>
                  <TableCell>{t('erp.agreement.margins.title')}</TableCell>
                  <TableCell>{t('erp.agreement.paymentTermDays')}</TableCell>
                  <TableCell align="right">—</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {agreements.map((agreement) => (
                  <TableRow key={agreement.uid}>
                    <TableCell>
                      <Chip
                        size="small"
                        label={agreement.status === 'active'
                          ? t('erp.agreement.active')
                          : t('erp.agreement.inactive')}
                        color={agreement.status === 'active' ? 'success' : 'default'}
                      />
                    </TableCell>
                    <TableCell>{scopeName(agreement)}</TableCell>
                    <TableCell>
                      {(agreement.margins || [])
                        .map((m) => `${serviceTypeLabel(m.serviceType)}: ${m.value}${m.feeType === 'percentage' ? '%' : ''}`)
                        .join(' · ') || '—'}
                    </TableCell>
                    <TableCell>{agreement.paymentCondition?.termDays ?? '—'}</TableCell>
                    <TableCell align="right">{toDateLabel(agreement.createdAt)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}
    </Box>
  );
};

export default ErpAgreementTab;
